import React, { useEffect, useState } from 'react';
import {
  Home as HomeOutlined,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Gradient } from '../AdminHeader/Gradient.js';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/index';

const gradient = new Gradient();

const SUPPORT_EMAIL = 'support@headshotsnow.ai';

const RefundPolicy = () => {
  const [currentLanguage, setCurrentLanguage] = useState('');

  const { t } = useTranslation();

  useEffect(() => {
    gradient.initGradient('#canvas-gradient');
    window.scrollTo(0, 0);

    return () => { };
  }, []);

  useEffect(() => {
    setCurrentLanguage(i18n.language);
  }, [i18n.language]);

  const homeHref = currentLanguage === 'en' ? '/' : `/${currentLanguage}/`;

  return (
    <>
      <main style={{ background: '#000' }}>
        <div className="landing-cover" data-controller="gradient-animation">
          <div className="container">
            <div className="d-flex flex-column align-items-center">
              <h1 className="display-1 fw-semi-bold text-white text-center mb-5 mb-sm-4">Refund Policy</h1>
              <Link
                to={homeHref}
                className="btn btn-soft-light btn-sm rounded-pill tag text-nowrap"
              >
                <HomeOutlined />
                {t('button.backHome')}
              </Link>
            </div>
          </div>
          <canvas id="canvas-gradient" data-transition-in />
          <div id="canvas-overlay" />
        </div>
      </main>
      <section className="bg-white text-black">
        <div className="container mx-auto p-6 max-w-4xl">
          <p className="mb-4">
            We want you to be happy with your AI-generated professional headshots. This Refund Policy explains when you
            can get a refund. By purchasing from HeadshotsNow.ai, you agree to this policy.
          </p>

          <div className="border-l-4 border-cyan-500 bg-cyan-50 p-4 mb-6">
            <p className="font-semibold mb-0">
              Our guarantee: If you do not receive at least one usable professional headshot, contact us at {SUPPORT_EMAIL}
              {' '}within 14 days of purchase. Refunds are approved when no final images have been downloaded and the
              request meets the conditions below.
            </p>
          </div>

          <h2 className="text-2xl font-bold mb-2">1. Eligibility Window</h2>
          <p className="mb-4">
            Refund requests must be submitted within <span className="font-semibold">14 days</span> of your purchase date.
          </p>

          <h2 className="text-2xl font-bold mb-2">2. Conditions for a Refund</h2>
          <ul className="list-disc list-inside mb-4" style={{ paddingLeft: '1.5em' }}>
            <li>Your order did not produce at least one usable professional headshot; and</li>
            <li>You have not downloaded any of the final generated images; and</li>
            <li>The request is made within the 14-day window with the email used at purchase.</li>
          </ul>

          <h2 className="text-2xl font-bold mb-2">3. How Downloads Affect Eligibility</h2>
          <p className="mb-4">
            Because headshots are a digital product, downloading final images is treated as accepting them. If you have
            downloaded one or more final headshots, the order is generally not eligible for a refund. If the AI did not
            produce a usable result, contact us — in many cases we will first re-run your generation at no extra cost.
          </p>

          <h2 className="text-2xl font-bold mb-2">4. How to Request a Refund</h2>
          <ol className="list-decimal list-inside mb-4" style={{ paddingLeft: '1.5em' }}>
            <li>Email {SUPPORT_EMAIL} from the address used for your order.</li>
            <li>Include your order number and a brief description of the issue.</li>
            <li>Our team will review and respond within 1–3 business days.</li>
          </ol>

          <h2 className="text-2xl font-bold mb-2">5. Processing Time</h2>
          <p className="mb-4">
            Approved refunds are issued to your original payment method. It typically takes 5–10 business days for the
            funds to appear, depending on your bank or card provider. We cannot refund to a different account.
          </p>

          <h2 className="text-2xl font-bold mb-2">6. Subscriptions</h2>
          <p className="mb-4">
            You may cancel a subscription at any time from your dashboard or the billing portal; cancellation takes effect
            at the end of the current billing period. We do not provide partial refunds for unused portions of a billing
            period unless required by law.
          </p>

          <h2 className="text-2xl font-bold mb-2">7. Abuse Prevention</h2>
          <p className="mb-4">
            Refunds are intended for customers who did not receive a usable result. We may decline requests that show
            signs of abuse — for example, downloading the images and then requesting a refund, repeated refund requests
            across multiple orders, or use that violates our <Link to="/acceptable-use" className="underline">Acceptable Use Policy</Link>.
          </p>

          <h2 className="text-2xl font-bold mb-2">8. Contact</h2>
          <p className="mb-4">
            Questions about this policy? Email {SUPPORT_EMAIL}. See also our{' '}
            <Link to="/delivery" className="underline">Delivery Policy</Link> and{' '}
            <Link to="/legal" className="underline">Terms &amp; Privacy</Link>.
          </p>

          <p className="mb-4 font-bold">Last updated: 18 June 2026</p>
        </div>
      </section>
    </>
  );
};

export default RefundPolicy;
